'use client';

import { useState, useRef, useCallback } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/common/Header';
import SiteFooter from './components/SiteFooter';
import BlogSection from './components/BlogSection';
import HeroV3 from './components/HeroV3';
import HomeStats from './components/HomeStats';
import HowItWorks from './components/HowItWorks';
import PlatformSection from './components/PlatformSection';

const SHOWCASE_TABS = [
  { label: 'HDR Processing', before: '/images/gallery/enbf.webp', after: '/images/gallery/enaf.webp' },
  { label: 'Day to Dusk', before: '/images/gallery/1ddbf.webp', after: '/images/gallery/1ddaf.webp' },
  { label: 'Virtual Staging', before: '/images/gallery/vsbefore.webp', after: '/images/gallery/vsafter.webp' },
  { label: 'Item Removal', before: '/images/gallery/item-removal-living-before.webp', after: '/images/gallery/item-removal-living-after.webp' },
  { label: 'Sky Replacement', before: '/images/gallery/enhancement-exterior-before.webp', after: '/images/gallery/enhancement-exterior-after.webp' },
];

function EditShowcaseSlider() {
  const [activeTab, setActiveTab] = useState(0);
  const [pos, setPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const active = SHOWCASE_TABS[activeTab];

  const updateFromClientX = useCallback((clientX: number) => {
    const el = sliderRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setIsDragging(true);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    updateFromClientX(e.clientX);
  };
  const endDrag = () => setIsDragging(false);

  return (
    <div className="relative">
      {/* Purple glow behind */}
      <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />

      <div
        ref={sliderRef}
        className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-gray-900 select-none touch-none cursor-ew-resize"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {/* Before image */}
        <img
          src={active.before}
          alt={`${active.label} before`}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/70 backdrop-blur-sm rounded-md text-white text-xs font-semibold pointer-events-none">
          Before
        </div>

        {/* After image (clipped) */}
        <div
          className="absolute inset-0 pointer-events-none will-change-[clip-path]"
          style={{
            clipPath: `polygon(${pos}% 0, 100% 0, 100% 100%, ${pos}% 100%)`,
            transition: isDragging ? 'none' : 'clip-path 120ms ease-out',
          }}
        >
          <img
            src={active.after}
            alt={`${active.label} after`}
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute top-3 right-3 px-2.5 py-1 bg-purple-600/80 backdrop-blur-sm rounded-md text-white text-xs font-semibold pointer-events-none">
            After
          </div>
        </div>

        {/* Handle */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white z-10 pointer-events-none will-change-[left]"
          style={{
            left: `${pos}%`,
            transform: 'translateX(-50%)',
            transition: isDragging ? 'none' : 'left 120ms ease-out',
          }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
            </svg>
          </div>
        </div>
      </div>

      {/* Service Tabs */}
      <div className="mt-4 flex gap-2 flex-wrap">
        {SHOWCASE_TABS.map((tab, idx) => (
          <button
            key={tab.label}
            onClick={() => {
              setActiveTab(idx);
              setPos(50);
            }}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
              idx === activeTab
                ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-lg shadow-purple-500/30'
                : 'bg-white/[0.04] border border-white/[0.1] text-gray-300 hover:bg-white/[0.08] hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ModernHomePage() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />

      {/* Hero V3 - Premium SaaS Hero */}
      <HeroV3 />

      {/* Trusted By + Animated Stats */}
      <HomeStats />

      {/* Services Section */}
      <section className="py-14 lg:py-16 bg-[#0F1419]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-purple-400 mb-3 uppercase tracking-wider">Our Services</p>
            <h2 className="text-4xl font-bold text-white mb-4">
              Professional Edits for Every Need
            </h2>
            <p className="text-lg text-gray-400">
              High-quality, affordable photo editing services for real estate professionals.<br />
              All edits are handled by expert photo editors.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { name: 'HDR Processing', img: '/images/gallery/enhancement-interior-after.webp', desc: 'Perfectly balanced exposures with rich details', href: '/services/hdr-photo-editing' },
              { name: 'Day to Dusk', img: '/images/gallery/day-dusk-residential-after.webp', desc: 'Transform daytime photos into stunning twilight', href: '/services/day-to-dusk' },
              { name: 'Virtual Staging', img: '/images/gallery/staging-living-after.webp', desc: 'Warm interior spaces beautifully', href: '/services/virtual-staging' },
              { name: 'Item Removal', img: '/images/gallery/item-removal-living-after.webp', desc: 'Remove unwanted objects seamlessly', href: '/services/item-removal' },
              { name: 'Sky Replacement', img: '/images/gallery/enhancement-exterior-after.webp', desc: 'Replace dull skies with vibrant ones', href: '/services/sky-replacement' },
              { name: 'And More', img: '/images/gallery/2ddaf.webp', desc: 'Portrait, Floor Plans, Enhancement & more', href: '/services' },
            ].map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.06 }}
                whileHover={{ y: -4, scale: 1.02 }}
              >
                <Link
                  href={service.href}
                  className="h-full flex flex-col p-3 bg-[#1A1F35] hover:bg-[#1E2440] border border-gray-800 hover:border-purple-500/30 rounded-2xl transition-colors cursor-pointer group overflow-hidden"
                >
                  <div className="relative w-full h-28 rounded-xl overflow-hidden mb-4">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={service.img}
                      alt={service.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1F35]/80 to-transparent" />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2 px-2">{service.name}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed mb-4 px-2">{service.desc}</p>
                  <span className="mt-auto px-2 text-purple-400 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more →
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Showcase Section */}
      <section className="py-14 lg:py-16 bg-[#0B0F1A] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Before/After Slider */}
            <EditShowcaseSlider />

            {/* Right - Content */}
            <div className="space-y-6">
              <div className="inline-block px-3 py-1 bg-purple-600/10 border border-purple-600/20 rounded-full">
                <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">See the Difference</span>
              </div>
              
              <h2 className="text-4xl font-bold text-white leading-tight">
                Professional Edits That <span className="text-purple-400">Win More Listings</span>
              </h2>
              
              <p className="text-gray-400 text-lg leading-relaxed">
                We enhance your images to highlight every detail, create visual impact, and help your properties stand out in a competitive market.
              </p>

              {/* Benefits Grid */}
              <div className="grid grid-cols-2 gap-6 pt-4">
                {[
                  { icon: '👁️', title: 'More Clicks', desc: 'Get more engagement and views with stunning images.' },
                  { icon: '🎯', title: 'Faster Sales', desc: 'Sell listings fast with properties that attract buyers.' },
                  { icon: '💰', title: 'Higher Value', desc: 'Professional edits increase perceived property value.' },
                  { icon: '😊', title: 'Happy Clients', desc: 'Impress your clients and grow your reputation.' },
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="text-3xl flex-shrink-0">{benefit.icon}</div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">{benefit.title}</h4>
                      <p className="text-gray-400 text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - platform workflow */}
      <HowItWorks />

      {/* Platform ecosystem */}
      <PlatformSection />

      {/* Testimonials Section */}
      <section className="py-14 lg:py-16 bg-[#0F1419]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-purple-400 mb-3 uppercase tracking-wider">Trusted by Professionals</p>
            <h2 className="text-4xl font-bold text-white mb-4">
              Loved by Real Estate Professionals Worldwide
            </h2>
          </div>

          {/* Testimonials Carousel */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "HDR is definitely my favorite. The edits look natural, and the colors don't feel overdone like some other services I've tried.",
                name: "Sarah Johnson",
                role: "Real Estate Photographer",
                avatar: "SJ"
              },
              {
                quote: "The quality is really solid for the price. I've tried a few editing companies before, and this has been the best balance between cost and consistent results.",
                name: "Michael Thompson",
                role: "Real Estate Agent",
                avatar: "MT"
              },
              {
                quote: "I honestly wasn't expecting the files back this quickly. I uploaded them late in the evening and had everything ready the next day. Really impressed with the turnaround.",
                name: "Emily Davis",
                role: "Broker",
                avatar: "ED"
              },
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-[#1A1F35] rounded-xl p-6 border border-gray-800">
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 text-sm mb-4 leading-relaxed">{testimonial.quote}</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">{testimonial.name}</div>
                    <div className="text-gray-400 text-xs">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link href="/auth/signup">
              <button className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all inline-flex items-center gap-2">
                Join 500+ Happy Clients
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-14 lg:py-16 bg-[#0B0F1A] border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-600/10 border border-purple-600/20 rounded-full mb-6">
            <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
            </svg>
            <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Get Started Today</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Elevate Your Real Estate Photography?
          </h2>
          
          <p className="text-xl text-gray-400 mb-8">
            Start your free trial today and get 3 free edits on sign up. No credit card required.
          </p>

          {/* Features List */}
          <div className="flex flex-col items-center gap-3 mb-10">
            {[
              '3 Free Edits',
              'No Credit Card Required',
              'Cancel Anytime',
              '4–12 Hour Turnaround',
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-gray-300">
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/auth/signup">
              <button className="px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white text-lg font-semibold rounded-lg transition-all inline-flex items-center gap-2">
                Start Free Trial
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </Link>
            <Link href="/services">
              <button className="px-8 py-4 bg-transparent hover:bg-white/5 text-white text-lg font-medium rounded-lg transition-all border border-gray-700">
                View Pricing
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog */}
      <BlogSection />

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
